import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Add as AddIcon,
  Business,
  People,
  Description,
} from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import api from "../services/api";
import ActionMenu from "../components/ui/ActionMenu";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
} from "@mui/material";

const validationSchema = Yup.object({
  name: Yup.string()
    .min(2, "Department name must be at least 2 characters")
    .required("Department name is required"),
  description: Yup.string()
    .min(10, "Description must be at least 10 characters")
    .required("Description is required"),
  managerId: Yup.string(),
});

export default function Departments() {
  const [open, setOpen] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [departmentToDelete, setDepartmentToDelete] = useState(null);

  const formik = useFormik({
    initialValues: {
      name: "",
      description: "",
      managerId: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setError("");
      setSuccess("");

      try {
        if (editingId) {
          await api.put(`/departments/${editingId}`, values);
          setSuccess("Department updated successfully!");
        } else {
          await api.post("/departments", values);
          setSuccess("Department created successfully!");
        }
        resetForm();
        setOpen(false);
        setEditingId(null);
        fetchDepartments();
      } catch (err) {
        setError(err.response?.data?.message || "Operation failed");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const fetchDepartments = async () => {
    try {
      setLoading(true);
      const response = await api.get("/departments");
      setDepartments(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Failed to fetch departments", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, []);

  const handleEdit = (department) => {
    setEditingId(department.id);
    formik.setValues({
      name: department.name,
      description: department.description,
      managerId: department.managerId || "",
    });
    setOpen(true);
  };

  const handleDeleteClick = (department) => {
    setDepartmentToDelete(department);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!departmentToDelete) return;
    try {
      await api.delete(`/departments/${departmentToDelete.id}`);
      setSuccess("Department deleted successfully!");
      setDeleteDialogOpen(false);
      setDepartmentToDelete(null);
      fetchDepartments();
    } catch (err) {
      setError("Failed to delete department");
      setDeleteDialogOpen(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setEditingId(null);
    formik.resetForm();
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-8 flex justify-between items-center flex-wrap gap-4"
      >
        <div>
          <h1 className="text-4xl font-bold text-primary mb-2">
            Department Management
          </h1>
          <p className="text-gray-600">Organize your teams and departments</p>
        </div>
        <motion.button
          onClick={() => {
            setEditingId(null);
            formik.resetForm();
            setOpen(true);
          }}
          className="btn-primary flex items-center gap-2 px-6 py-3"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <AddIcon />
          <span>Add Department</span>
        </motion.button>
      </motion.div>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 3 }}>
          {success}
        </Alert>
      )}

      {/* Departments Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="card">
              <div className="flex items-center justify-between mb-4">
                <div className="w-16 h-16 skeleton rounded-2xl"></div>
                <div className="w-8 h-8 skeleton rounded-xl"></div>
              </div>
              <div className="h-6 skeleton rounded mb-2 w-3/4"></div>
              <div className="h-4 skeleton rounded w-full mb-2"></div>
              <div className="h-4 skeleton rounded w-2/3"></div>
            </div>
          ))}
        </div>
      ) : departments.length === 0 ? (
        <div className="card text-center py-12">
          <Business className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No departments found</p>
        </div>
      ) : (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {departments.map((dept, index) => (
            <motion.div
              key={dept.id}
              variants={cardVariants}
              className="card group cursor-pointer"
              whileHover={{ y: -8, transition: { duration: 0.2 } }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center text-white shadow-lg">
                    <Business className="w-8 h-8" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-1">
                      {dept.name}
                    </h3>
                    <p className="text-sm text-gray-500 flex items-center gap-1">
                      <People className="w-4 h-4" />
                      {dept.employeeCount || 0} employees
                    </p>
                  </div>
                </div>
                <ActionMenu
                  onEdit={handleEdit}
                  onDelete={handleDeleteClick}
                  item={dept}
                />
              </div>

              <div className="pt-4 border-t border-gray-100">
                <div className="flex items-start gap-3 text-sm">
                  <Description className="w-4 h-4 text-gray-400 mt-0.5" />
                  <p className="text-gray-600 line-clamp-2">
                    {dept.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle sx={{ fontSize: "1.5rem", fontWeight: 600 }}>
            {editingId ? "Edit Department" : "Add Department"}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="name"
                  name="name"
                  label="Department Name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  id="description"
                  name="description"
                  label="Description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.description &&
                    Boolean(formik.errors.description)
                  }
                  helperText={
                    formik.touched.description && formik.errors.description
                  }
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="managerId"
                  name="managerId"
                  label="Manager ID (Optional)"
                  value={formik.values.managerId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2 }}>
            <Button onClick={handleClose} sx={{ borderRadius: 2 }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={formik.isSubmitting}
              sx={{ borderRadius: 2 }}
            >
              {formik.isSubmitting ? "Saving..." : "Save"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <p>
            Are you sure you want to delete "{departmentToDelete?.name}"? This
            action cannot be undone.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
